import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import Tooltip from '../../components/Tooltip';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import topicPath from '../../utils/url/topicPath';
import ContentMapColumnHeader from '../ContentMapColumnHeader';
import TopicNameWithIcon from '../TopicNameWithIcon';
import useTopicDrop from '../useTopicDrop';
import {
  DragTypes,
  isTopicPostPinned,
  MoveStates,
  SelectedPostsShape,
  TopicReorganizationMetadataShape,
  TopicReorganizationTreeShape,
  TopicSelectedShape,
  TopicStatus,
} from '../useTopicReorganization';
import styles from './TopicTreeCard.module.scss';
import TopicTreeCardDropSeparatorStack from './TopicTreeCardDropSeparatorStack';
import TopicTreeCardItem from './TopicTreeCardItem';
import TopicTreeCardItemDraggable from './TopicTreeCardItemDraggable';
import TopicTreeCardMenu from './TopicTreeCardMenu';
import TopicTreeCardPost from './TopicTreeCardPost';

const CARD_LEVEL_INDENT = parseInt(styles.cardLevelIndent, 10);
const POST_HEIGHT = parseInt(styles.postHeight, 10);
const POST_LIST_BOTTOM_SPACING = parseInt(styles.postListBottomSpacing, 10);
const TOPIC_HEIGHT = parseInt(styles.topicHeight, 10);

export const getCardHeight = (postCount) =>
  TOPIC_HEIGHT +
  postCount * POST_HEIGHT +
  (postCount > 0 ? POST_LIST_BOTTOM_SPACING : 0);

const getRowKey = (key, reorganizationMetadata) => {
  const { move, isPinned } = reorganizationMetadata;
  const rowKey = [key, move.state].join('-');

  // Toggling pinned status should re-create the component,
  // otherwise React DnD's item type won't change:
  // https://github.com/react-dnd/react-dnd/issues/1860
  if (typeof isPinned !== 'boolean') return rowKey;
  return `${rowKey}-${isPinned ? 'pinned' : 'unpinned'}`;
};

const propTypes = {
  header: PropTypes.bool,
  specialColumn: PropTypes.oneOf(['posts', 'unorganized']),
  tree: TopicReorganizationTreeShape.isRequired,
  showPosts: PropTypes.bool.isRequired,
  reorganizable: PropTypes.bool.isRequired,
  isReorganizing: PropTypes.bool.isRequired,
  getDropParams: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  columnSelected: TopicSelectedShape.isRequired,
  onTopicSelectedChange: PropTypes.func.isRequired,
  onTopicDeletedChange: PropTypes.func.isRequired,
  onPostPinnedChange: PropTypes.func.isRequired,
  onPostSelectedChange: PropTypes.func.isRequired,
  onPostDeletedChange: PropTypes.func.isRequired,
  onPostPublishedChange: PropTypes.func.isRequired,
  onPostArchivedChange: PropTypes.func.isRequired,
  onTopicNameChange: PropTypes.func.isRequired,
  ancestorsReorganizationMetadata: PropTypes.arrayOf(
    TopicReorganizationMetadataShape,
  ).isRequired,
  selectedPosts: SelectedPostsShape.isRequired,
  onDragOverChange: PropTypes.func.isRequired,
  dragOverTopicId: PropTypes.string,
  expanded: PropTypes.bool.isRequired,
  onExpandedChange: PropTypes.func.isRequired,
  onSubtopicCreate: PropTypes.func.isRequired,
  initialRenaming: PropTypes.bool,
};

const defaultProps = {
  header: false,
  specialColumn: null,
  dragOverTopicId: null,
  initialRenaming: false,
};

const TopicTreeCard = ({
  header,
  specialColumn,
  showPosts,
  tree,
  reorganizable,
  isReorganizing,
  columnSelected,
  getDropParams,
  onTopicSelectedChange,
  onTopicDeletedChange,
  onTopicNameChange,
  onSubtopicCreate,
  onPostPinnedChange,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostPublishedChange,
  onPostArchivedChange,
  ancestorsReorganizationMetadata,
  selectedPosts,
  onDragOverChange,
  dragOverTopicId,
  expanded,
  onExpandedChange,
  initialRenaming,
}) => {
  let accept = [
    DragTypes.column,
    DragTypes.mixed,
    DragTypes.pinnedPosts,
    DragTypes.posts,
    DragTypes.topics,
  ];
  if (specialColumn === 'posts') {
    accept = [DragTypes.pinnedPosts, DragTypes.posts];
  } else if (specialColumn === 'unorganized') {
    accept = [];
  }
  const dropRef = useTopicDrop(accept, {
    tree,
    getDropParams,
    onDragOverChange,
    topicId: tree.topic.id,
  });

  const { reorganizationMetadata } = tree;

  const [renaming, setRenaming] = useState(
    initialRenaming ||
      (reorganizationMetadata.isNew && !reorganizationMetadata.name),
  );
  const [newName, setNewName] = useState('');
  const hasSubtopics = tree.children.length > 0;

  const isMovedOut = reorganizationMetadata.move.state === MoveStates.movedOut;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const deleted =
    !isMovedOut && reorganizationMetadata.status === TopicStatus.deleted;
  const name = isMovedOut
    ? tree.topic.name
    : reorganizationMetadata.name || tree.topic.name;

  const cancelRenaming = () => {
    setRenaming(false);
    setNewName('');
  };

  const saveTopicName = () => {
    onTopicNameChange({
      id: tree.topic.id,
      name: newName === tree.topic.name ? null : newName,
    });
    cancelRenaming();
  };

  let cardContentInner = null;
  const hasPosts = showPosts && tree.topicPosts.length > 0;
  if (expanded && hasPosts) {
    const lastPinnedPostIndex = _.findLastIndex(
      tree.topicPosts,
      isTopicPostPinned,
    );

    cardContentInner = (
      <div className={styles.postList}>
        {tree.topicPosts.map((topicPost, index) => (
          <TopicTreeCardPost
            key={getRowKey(topicPost.postKey, topicPost.reorganizationMetadata)}
            reorganizable={reorganizable}
            ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
            topicPost={topicPost}
            index={index}
            lastPinnedPostIndex={lastPinnedPostIndex}
            tree={tree}
            isReorganizing={isReorganizing}
            columnSelected={columnSelected}
            selectedPosts={selectedPosts}
            getDropParams={getDropParams}
            onPostSelectedChange={onPostSelectedChange}
            onPostDeletedChange={onPostDeletedChange}
            onPostPinnedChange={onPostPinnedChange}
            onPostPublishedChange={onPostPublishedChange}
            onPostArchivedChange={onPostArchivedChange}
            onTopicSelectedChange={onTopicSelectedChange}
            onTopicDeletedChange={onTopicDeletedChange}
            onTopicNameChange={onTopicNameChange}
            onSubtopicCreate={onSubtopicCreate}
          />
        ))}
      </div>
    );
  }

  let topicContent = null;
  if (renaming) {
    topicContent = (
      <ClickOutsideHandler onClickOutside={cancelRenaming}>
        <form
          onSubmit={(e) => {
            saveTopicName();
            e.preventDefault();
          }}
          className={classNames(styles.renameForm, {
            [styles.hasPosts]: showPosts && tree.topicPosts.length > 0,
          })}
        >
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            type="text"
            placeholder={name}
            onKeyDown={({ key }) => {
              if (key === 'Escape') cancelRenaming();
            }}
            aria-label="Rename topic"
          />
          <Button
            variant="primary"
            disabled={!newName}
            size="small"
            onClick={saveTopicName}
          >
            Save
          </Button>
        </form>
      </ClickOutsideHandler>
    );
  } else {
    const topicTitle = (
      <div className={styles.title}>
        {header ? (
          <ContentMapColumnHeader
            movedIn={reorganizationMetadata.move.state === MoveStates.movedIn}
            topic={tree.topic}
            name={name}
          />
        ) : (
          <TopicNameWithIcon topic={tree.topic} name={name} truncate="single" />
        )}
      </div>
    );
    topicContent = specialColumn ? (
      <div className={styles.nonDraggableTitle}>{topicTitle}</div>
    ) : (
      <TopicTreeCardItemDraggable
        type="topic"
        to={topicPath(tree.topic, 'subtopics')}
        onCheckboxChange={() =>
          onTopicSelectedChange({
            id: tree.topic.id,
            selected: !tree.isSelected,
          })
        }
        reorganizable={reorganizable}
        isChecked={tree.isSelected}
        isCheckboxDisabled={tree.isParentSelected}
        isReorganizing={isReorganizing}
        isMovedOut={isMovedOut}
        toMove={
          tree.isSelected
            ? columnSelected
            : {
                postKeys: [],
                pinnedPostKeys: [],
                topicIds: [tree.topic.id],
              }
        }
      >
        {topicTitle}
      </TopicTreeCardItemDraggable>
    );
  }

  const cardContent = (
    <div
      className={classNames(styles.cardContentInner, {
        [styles.disabled]: !reorganizable && isReorganizing,
      })}
    >
      <TopicTreeCardItem
        type="topic"
        reorganizationMetadata={reorganizationMetadata}
        ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
        originalName={tree.topic.name}
        titleLine={
          <div
            className={classNames(styles.topicLine, {
              [styles.deleted]: deleted,
            })}
          >
            <div className={styles.topic}>{topicContent}</div>
          </div>
        }
        controls={
          !header &&
          !renaming &&
          (hasSubtopics || hasPosts) && (
            <Button
              onClick={() => onExpandedChange()}
              className={styles.expandButton}
              block
            >
              <Caret direction={expanded ? 'up' : 'down'} size={10} />
            </Button>
          )
        }
        menu={
          !renaming &&
          !specialColumn &&
          !isMovedOut && (
            <TopicTreeCardMenu
              isReorganizing={isReorganizing}
              deleted={deleted}
              header={header}
              topic={tree.topic}
              hasSubtopics={hasSubtopics}
              hasPosts={hasPosts}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onSubtopicCreate={onSubtopicCreate}
              onTopicDeletedChange={onTopicDeletedChange}
              onRenameButtonClick={() => {
                setRenaming(true);
                setNewName(name);
              }}
            />
          )
        }
      >
        {cardContentInner}
      </TopicTreeCardItem>
    </div>
  );

  return (
    <div>
      <div ref={reorganizable ? dropRef : undefined}>
        <div
          className={classNames(styles.card, {
            [styles.isDragOver]:
              !isMovedOut && dragOverTopicId === tree.topic.id,
          })}
          style={{
            marginLeft:
              CARD_LEVEL_INDENT * ancestorsReorganizationMetadata.length,
          }}
        >
          <div className={styles.cardContent}>
            {!isReorganizing || reorganizable ? (
              cardContent
            ) : (
              <Tooltip
                display="block"
                tooltip="You must be a topic owner to take action on this topic or its posts"
                trigger="hover"
                placement="top"
                popOverPortal={document.body}
              >
                {cardContent}
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {isReorganizing && (
        <TopicTreeCardDropSeparatorStack
          tree={tree}
          expanded={expanded}
          getDropParams={getDropParams}
        />
      )}
    </div>
  );
};

TopicTreeCard.propTypes = propTypes;
TopicTreeCard.defaultProps = defaultProps;

export default TopicTreeCard;
